import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE_URL, getAuthToken } from '@/services/api';

export const LiveVMOSScreenshot = ({
  padId,
  interval = 2000,
  quality = 70,
  disabled = false,
  className = "",
}: {
  padId: string;
  interval?: number;
  quality?: number;
  disabled?: boolean;
  className?: string;
}) => {
  const prevUrlRef = useRef<string | null>(null);

  const { data: imageSrc, isLoading, isError } = useQuery({
    queryKey: ['vmos-screenshot', padId, quality],
    queryFn: async () => {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${API_BASE_URL}/vmos-group-control/devices/${padId}/screenshot?quality=${quality}&t=${Date.now()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Failed to fetch screenshot');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Revoke the previous URL for THIS instance only. Do NOT revoke on
      // unmount: the URL is React Query cache data and revoking it would leave
      // a cached `blob:` src pointing at a freed object.
      if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
      prevUrlRef.current = url;
      return url;
    },
    refetchInterval: disabled ? false : interval,
    enabled: !disabled,
    retry: 2,
    staleTime: 0,
  });

  if (isLoading && !imageSrc) {
    return (
      <div className={cn("flex items-center justify-center bg-muted rounded-lg", className)}>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError && !imageSrc) {
    return (
      <div className={cn("flex items-center justify-center bg-muted rounded-lg", className)}>
        <span className="text-xs text-muted-foreground">No screenshot</span>
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden rounded-lg bg-black", className)}>
      {imageSrc && (
        <img
          src={imageSrc}
          alt="Device screenshot"
          className="w-full h-full object-contain"
          draggable={false}
        />
      )}
    </div>
  );
};
